import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Save, Loader2, Home, FileText, MapPin, HelpCircle, Scale, BarChart3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const PAGE_LABELS = {
  home: { label: 'Ana Sayfa', icon: Home },
  header: { label: 'Header (Ust Menu)', icon: FileText },
  footer: { label: 'Footer', icon: FileText },
  contact: { label: 'Iletisim', icon: MapPin },
  about: { label: 'Hakkimizda', icon: FileText },
  legal: { label: 'Yasal Sayfalar', icon: Scale },
};

const BLOCK_LABELS = {
  // Home
  hero_label: 'Hero Ust Etiket',
  hero_title: 'Hero Baslik',
  hero_subtitle: 'Hero Alt Baslik',
  hero_cta: 'Hero CTA Buton Metni',
  hero_image: 'Hero Gorsel URL',
  stat_1_value: 'Istatistik 1 - Deger',
  stat_1_label: 'Istatistik 1 - Etiket',
  stat_2_value: 'Istatistik 2 - Deger',
  stat_2_label: 'Istatistik 2 - Etiket',
  stat_3_value: 'Istatistik 3 - Deger',
  stat_3_label: 'Istatistik 3 - Etiket',
  how_step1_title: 'Nasil Calisir 1 - Baslik',
  how_step1_desc: 'Nasil Calisir 1 - Aciklama',
  how_step2_title: 'Nasil Calisir 2 - Baslik',
  how_step2_desc: 'Nasil Calisir 2 - Aciklama',
  how_step3_title: 'Nasil Calisir 3 - Baslik',
  how_step3_desc: 'Nasil Calisir 3 - Aciklama',
  trust_1: 'Guven Rozeti 1',
  trust_2: 'Guven Rozeti 2',
  trust_3: 'Guven Rozeti 3',
  faq_items: 'SSS (JSON)',
  // Footer
  company_name: 'Sirket Adi',
  address: 'Adres',
  phone: 'Telefon',
  email: 'E-posta',
  // Legal
  kvkk: 'KVKK Metni (HTML)',
  'aydinlatma-metni': 'Aydinlatma Metni (HTML)',
  'cerez-politikasi': 'Cerez Politikasi (HTML)',
  'sorumluluk-metni': 'Sorumluluk Metni (HTML)',
  // Header
  cta_text: 'CTA Buton Metni',
  cta_link: 'CTA Buton Linki (orn: tel:+90...)',
  // About
  title: 'Sayfa Basligi',
  content: 'Sayfa Icerigi (HTML)',
};

// Blocks that should use textarea
const TEXTAREA_BLOCKS = new Set([
  'hero_subtitle', 'how_step1_desc', 'how_step2_desc', 'how_step3_desc',
  'faq_items', 'kvkk', 'aydinlatma-metni', 'cerez-politikasi', 'sorumluluk-metni', 'address', 'content'
]);

// Order blocks within each page group
const BLOCK_ORDER = {
  home: ['hero_label', 'hero_title', 'hero_subtitle', 'hero_cta', 'hero_image',
    'stat_1_value', 'stat_1_label', 'stat_2_value', 'stat_2_label', 'stat_3_value', 'stat_3_label',
    'how_step1_title', 'how_step1_desc', 'how_step2_title', 'how_step2_desc', 'how_step3_title', 'how_step3_desc',
    'trust_1', 'trust_2', 'trust_3', 'faq_items'],
  footer: ['company_name', 'address', 'phone', 'email'],
  contact: ['phone', 'email', 'address'],
  legal: ['kvkk', 'aydinlatma-metni', 'cerez-politikasi', 'sorumluluk-metni'],
  header: ['cta_text', 'cta_link', 'phone'],
  about: ['title', 'content'],
};

export default function CMSPage() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getContent().then(r => setBlocks(r.data)).finally(() => setLoading(false));
  }, []);

  const updateBlock = (index, value) => {
    setBlocks(prev => prev.map((b, i) => i === index ? { ...b, value } : b));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateContent(blocks.map(b => ({ page_key: b.page_key, block_key: b.block_key, value: b.value })));
      toast.success('Icerikler kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  // Group and order blocks
  const grouped = {};
  blocks.forEach(b => {
    if (!grouped[b.page_key]) grouped[b.page_key] = [];
    grouped[b.page_key].push(b);
  });

  // Sort blocks within each group
  Object.keys(grouped).forEach(pageKey => {
    const order = BLOCK_ORDER[pageKey] || [];
    grouped[pageKey].sort((a, b) => {
      const ai = order.indexOf(a.block_key);
      const bi = order.indexOf(b.block_key);
      if (ai === -1 && bi === -1) return a.block_key.localeCompare(b.block_key);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  });

  const pageOrder = ['home', 'header', 'footer', 'contact', 'about', 'legal'];

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div data-testid="admin-cms">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Icerik Yonetimi (CMS)</h1>
          <p className="text-sm text-muted-foreground mt-1">Ana sayfa, footer, iletisim ve yasal sayfa iceriklerini duzenleyin.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="cms-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Tumu Kaydet
        </Button>
      </div>

      {pageOrder.map(pageKey => {
        const pageBlocks = grouped[pageKey];
        if (!pageBlocks || pageBlocks.length === 0) return null;
        const pageInfo = PAGE_LABELS[pageKey] || { label: pageKey, icon: FileText };
        const PageIcon = pageInfo.icon;

        return (
          <Card key={pageKey} className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PageIcon className="h-5 w-5" />
                {pageInfo.label}
                <span className="text-xs text-muted-foreground font-normal ml-2">({pageBlocks.length} alan)</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {pageBlocks.map((b) => {
                const globalIdx = blocks.indexOf(b);
                const label = BLOCK_LABELS[b.block_key] || b.block_key;
                const isTextarea = TEXTAREA_BLOCKS.has(b.block_key) || (b.value && b.value.length > 100);
                const isJson = b.block_key === 'faq_items';
                const isHtml = ['kvkk', 'aydinlatma-metni', 'cerez-politikasi'].includes(b.block_key);

                return (
                  <div key={b.block_key}>
                    <div className="flex items-center gap-2 mb-1">
                      <Label className="text-sm font-medium">{label}</Label>
                      {isJson && <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded">JSON</span>}
                      {isHtml && <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">HTML</span>}
                    </div>
                    {isTextarea || isJson || isHtml ? (
                      <textarea
                        value={b.value || ''}
                        onChange={e => updateBlock(globalIdx, e.target.value)}
                        rows={isJson ? 8 : isHtml ? 10 : 3}
                        className={`flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ${isJson || isHtml ? 'font-mono text-xs' : ''}`}
                      />
                    ) : (
                      <Input value={b.value || ''} onChange={e => updateBlock(globalIdx, e.target.value)} />
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">{b.page_key}.{b.block_key}</p>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}

      {/* Render any other page groups not in pageOrder */}
      {Object.keys(grouped).filter(k => !pageOrder.includes(k)).map(pageKey => {
        const pageBlocks = grouped[pageKey];
        return (
          <Card key={pageKey} className="mb-6">
            <CardHeader><CardTitle>{pageKey}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {pageBlocks.map(b => {
                const globalIdx = blocks.indexOf(b);
                return (
                  <div key={b.block_key}>
                    <Label className="text-sm">{BLOCK_LABELS[b.block_key] || b.block_key}</Label>
                    <Input value={b.value || ''} onChange={e => updateBlock(globalIdx, e.target.value)} />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
